import { useEffect, useMemo, useState, type FormEvent } from 'react'
import type { UIMessage } from 'ai'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport } from 'ai'
import clsx from 'clsx'
import Spinner from '@tandem/ui/Spinner'
import { useTRPC, type RouterInputs, type RouterOutputs } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import $layout from '@tandem/ui/layout.css'
import $type from '@tandem/ui/type.css'
import $table from '@tandem/ui/table.css'
import $form from '@tandem/ui/form.css'
import Md from '../../Md'
import $ from './-Ask.css'

// Вопрос к данным одной схемы: та же цепочка dbq для всех источников. Под ответом со
// сверкой — «верно» / «неверно»: так память растёт в золотой набор.
export const Route = createFileRoute('/ask/$schema')({
  component: AskPage,
})

type Schema = RouterInputs['layer']['graph']['schema']
type ChatId = `${string}-${string}-${string}-${string}-${string}`

let FEEDBACK_RU: Record<RouterOutputs['layer']['feedback'], string> = {
  verified: 'Подтверждено: ответ и его определения — в памяти',
  proposed: 'Отправлено администратору на подтверждение',
  stale: 'Отмечено как неверный — напишите, что не так, и спросите снова',
}

let cell = (v: unknown): string =>
  v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)

function ResultTable({ columns, rows }: { columns: string[]; rows?: Record<string, unknown>[] }) {
  if (!Array.isArray(rows)) return <p class={$type.muted}>Строки результата не сохраняются в истории.</p>
  if (!rows.length) return <p class={$type.muted}>Ничего не найдено.</p>
  return (
    <div class={$table.scroller}>
      <table class={$table.table}>
        <thead><tr>{columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>{columns.map(c => <td key={c}>{cell(row[c])}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Feedback({ schema, answerKey }: { schema: Schema; answerKey: string }) {
  let trpc = useTRPC()
  let feedback = useMutation(trpc.layer.feedback.mutationOptions())
  if (feedback.data) return <div class={$type.muted}>{FEEDBACK_RU[feedback.data]}</div>
  return (
    <div class={$.feedback}>
      <button type='button' class='plain' disabled={feedback.isPending} onClick={() => feedback.mutate({ schema, key: answerKey, ok: true })}>верно</button>
      <button type='button' class='plain' disabled={feedback.isPending} onClick={() => feedback.mutate({ schema, key: answerKey, ok: false })}>неверно</button>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ToolStep({ schema, name, part }: { schema: Schema; name: string; part: any }) {
  let done = part.state === 'output-available'
  let out = part.output
  if (name === 'run_sql' || name === 'answer') return (
    <div class={$.tool}>
      <div class={$type.muted}>▸ {name === 'answer' ? 'Итог со сверкой' : 'SQL'}{!done && <> <Spinner /></>}</div>
      {part.input?.sql && <pre class={$.sql}>{part.input.sql}</pre>}
      {done && (out.ok ?
        <>
          <div class={$type.muted}>{out.rowCount} строк</div>
          <ResultTable columns={out.columns} rows={out.rows} />
          {out.note && <pre class={$.sql}>{out.note}</pre>}
          {out.key && <Feedback schema={schema} answerKey={out.key} />}
        </>
      : <p class={$type.error}>Ошибка: {out.error}</p>)}
    </div>
  )
  return (
    <div class={clsx($.tool, $type.muted)}>
      ▸ {name}: «{part.input?.question ?? part.input?.table ?? part.input?.text ?? ''}»{!done && <> <Spinner /></>}
      {done && Array.isArray(out) && ` — ${out.length} кандидатов`}
    </div>
  )
}

function RulesPanel({ schema }: { schema: Schema }) {
  let trpc = useTRPC()
  let { data: saved } = useQuery(trpc.layer.rules.queryOptions({ schema }))
  let save = useMutation(trpc.layer.setRules.mutationOptions())
  let [text, setText] = useState('')
  useEffect(() => { if (saved != null) setText(saved) }, [saved])
  let dirty = saved != null && text !== saved

  return (
    <details class={$.rules}>
      <summary class={$type.muted}>Бизнес-правила {saved ? '✓' : ''}</summary>
      <p class={$type.footnote}>Дополняют системный промпт: политики, что предпочитать, чего не брать. Определения терминов лучше давать в диалоге — они попадают в память.</p>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder='Например: суммы показывай без НДС; период по умолчанию — текущий месяц.'
        rows={6}
      />
      <button
        class={$form.button}
        disabled={!dirty || save.isPending}
        onClick={() => save.mutate({ schema, rules: text })}
      >
        {save.isPending ? <Spinner /> : 'Сохранить'}
      </button>
    </details>
  )
}

// Каждый тред — своя сессия assistant-runtime; история хранится на сервере, ключ
// треда — client_id. Список тредов пользователя — в сайдбаре, как в каталоге.
function AskPage() {
  let { schema } = Route.useParams() as { schema: Schema }
  let { company } = useAuth()
  let trpc = useTRPC()
  let enabled = company?.features?.includes(schema) ?? false
  let isAdmin = company?.role === 'admin'
  let [thread, setThread] = useState<{ id: ChatId; fresh: boolean }>(() => ({ id: crypto.randomUUID(), fresh: true }))
  let { data: threads } = useQuery({ ...trpc.layer.threads.queryOptions({ schema }), enabled })
  let { data: initial } = useQuery({
    ...trpc.layer.thread.queryOptions({ schema, id: thread.id }),
    enabled: enabled && !thread.fresh,
    staleTime: Infinity,
  })

  if (!enabled) return (
    <div class={$layout.container}><p class={$type.muted}>Раздел недоступен.</p></div>
  )

  return (
    <div class={clsx($layout.container, $.layout)}>
      <aside class={$.sidebar}>
        <button type='button' class={$form.button} onClick={() => setThread({ id: crypto.randomUUID(), fresh: true })}>
          Новый вопрос
        </button>
        <nav class={$.threads}>
          {(threads ?? []).map(t => (
            <button
              key={t.clientId}
              type='button'
              class={clsx('plain', $.thread, t.clientId === thread.id && $.threadActive)}
              onClick={() => setThread({ id: t.clientId as ChatId, fresh: false })}
            >
              <span class={$.threadTitle}>{t.title}</span>
              <span class={$type.footnote}>{new Date(t.updatedAt).toLocaleDateString('ru-RU')}</span>
            </button>
          ))}
        </nav>
      </aside>
      {(thread.fresh || initial) && (
        <ChatPane key={thread.id} schema={schema} thread={thread.id} initial={(initial ?? []) as unknown as UIMessage[]} isAdmin={isAdmin} companyId={company?.company_id} />
      )}
    </div>
  )
}

function ChatPane({ schema, thread, initial, isAdmin, companyId }: {
  schema: Schema; thread: ChatId; initial: UIMessage[]; isAdmin: boolean; companyId?: number
}) {
  let trpc = useTRPC()
  let qc = useQueryClient()
  let transport = useMemo(() => new DefaultChatTransport({
    api: `${env.API_URL}/assistant/${schema}/chat`,
    credentials: 'include',
    headers: companyId != null ? { 'x-company-id': String(companyId) } : undefined,
  }), [schema, companyId])
  let { messages, sendMessage, status, stop, error } = useChat({ id: thread, messages: initial, transport })
  let [input, setInput] = useState('')
  let busy = status === 'submitted' || status === 'streaming'
  useEffect(() => {
    if (status === 'ready' && messages.length) void qc.invalidateQueries({ queryKey: trpc.layer.threads.queryKey({ schema }) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  let submit = (e: FormEvent) => {
    e.preventDefault()
    let t = input.trim()
    if (t && !busy) { sendMessage({ text: t }); setInput('') }
  }

  return (
    <div class={$.chat}>
      <h3>Вопрос к данным</h3>

      {isAdmin && <RulesPanel schema={schema} />}

      <div class={$.log}>
        {messages.map(m => (
          <div key={m.id} class={m.role === 'user' ? $.user : $.assistant}>
            {m.parts.map((part, i) =>
              part.type === 'text' ? <div key={i} class={$.md}><Md>{part.text}</Md></div>
              : part.type.startsWith('tool-') ? <ToolStep key={i} schema={schema} name={part.type.slice(5)} part={part} />
              : null
            )}
          </div>
        ))}
        {status === 'submitted' && <div class={$type.muted}><Spinner /></div>}
        {error && <p class={$type.error}>Ошибка: {error.message}</p>}
      </div>

      <form onSubmit={submit} class={$.form}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) submit(e) }}
          placeholder='Например: выручка по магазинам за прошлый месяц'
          rows={2}
        />
        {busy
          ? <button type='button' class={$form.button} onClick={stop}>Стоп</button>
          : <button class={$form.button} disabled={!input.trim()}>Спросить</button>}
      </form>
    </div>
  )
}
